import { Api } from 'api';
import { useEffect, useState } from 'react';
import { Stream } from 'types/PerformanceReturns';
import {
  getPerformanceReturnColumns,
  getPerformanceReturnCompareColumns,
} from './PerformanceReturnsColumns';

type Props = {
  benchmarkId?: string;
  drillDownHandler?: (date: string) => void;
};

export function usePerformanceReturnsData({
  benchmarkId,
  drillDownHandler,
}: Props) {
  const [startDate, setStartDate] = useState<Date>();
  const [calcFrequency, setCalcFrequency] = useState<any>(null);
  const [endDate, setEndDate] = useState<Date>();
  const [stream, setStream] = useState<Stream | null>(null);
  const [streamCompare, setStreamCompare] = useState<Stream | null>(null);
  const [tableData, setTableData] = useState<any>();
  const [tableColumns, setTableColumns] = useState<any>([]);

  const { data: frequencies } = Api.lookup.GetCalculationFrequencies();
  const { data: streams } = Api.streams.GetStreams(benchmarkId);
  const { data: streamDetails, isLoading: isStreamDetailsLoading } =
    Api.streams.GetStreamDetails({
      streamId: stream?.streamId,
      calcFrequency: calcFrequency?.id,
      startDate,
      endDate,
    });
  const { data: streamDetailsCompare, isLoading: isStreamDetailsCompLoading } =
    Api.streams.GetStreamDetails({
      streamId: streamCompare?.streamId,
      calcFrequency: calcFrequency?.id,
      startDate,
      endDate,
    });

  function calcFrequencyHandler(data: any) {
    setCalcFrequency(data);
  }

  function startDateHandler(date: any) {
    if (date) {
      setStartDate(date);
    }
  }

  function endDateHandler(date: any) {
    if (date) {
      setEndDate(date);
    }
  }

  function resetFilters() {
    setStream(null);
    setEndDate(undefined);
    setStartDate(undefined);
    setStreamCompare(null);
    setCalcFrequency(null);
  }

  useEffect(() => {
    if (streamDetails && streamDetailsCompare) {
      const perfReturns = streamDetails.performanceReturns;
      const perfReturnsComp = streamDetailsCompare.performanceReturns;
      const values = perfReturns?.map((data) => {
        const value = data.value;
        const valueCompare =
          perfReturnsComp.find((val) => val.date === data.date)?.value ?? 0;
        const difference = value - valueCompare;

        return {
          date: data.date,
          value,
          valueCompare,
          difference,
        };
      });

      setTableData(values);
    } else if (streamDetails) {
      setTableData(streamDetails.performanceReturns);
    } else {
      setTableData(undefined);
    }
  }, [streamDetails, streamDetailsCompare]);

  useEffect(() => {
    if (stream && streamCompare) {
      setTableColumns(
        getPerformanceReturnCompareColumns(
          stream,
          streamCompare,
          drillDownHandler
        )
      );
    } else if (stream) {
      setTableColumns(getPerformanceReturnColumns(drillDownHandler));
    } else {
      setTableColumns(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stream, streamCompare]);

  return {
    isLoading: isStreamDetailsLoading || isStreamDetailsCompLoading,
    streams,
    tableData,
    tableColumns,
    frequencies,
    startDate,
    startDateHandler,
    endDate,
    endDateHandler,
    calcFrequency,
    calcFrequencyHandler,
    stream,
    setStream,
    streamCompare,
    setStreamCompare,
    resetFilters,
  };
}
